/**
 * Avatar configuration: the player character GLB + Mixamo animation track names
 * and placement corrections.
 *
 * The GLB is produced offline by heroblender/mixamo_glb_merger: Idle/Walking/
 * Run/Jump are baked into a single player.glb so the runtime loads one asset.
 * `avatarRotationOffset` corrects a model that faces the wrong way (Mixamo
 * characters typically face +Z; if the controller's forward is -Z, set 180).
 * `eyeHeight` must match CharacterController.eyeHeight. `groundOffset` mirrors
 * CharacterController.hoverHeight so the visible model is placed at the ground
 * while the collision capsule keeps its small hover clearance.
 */

export interface AvatarConfig {
    /** Runtime URL of the baked player.glb. */
    avatarUrl: string;
    /** Anim track names inside the GLB (state → track). */
    idleAnim: string;
    walkAnim: string;
    runAnim: string;
    jumpAnim: string;
    /** Extra one-shot action clips exposed as HUD buttons. */
    actionAnims: ReadonlyArray<{ name: string; label: string; duration?: number; loop?: boolean }>;
    /** Uniform scale applied to the model. */
    avatarScale: number;
    /** Yaw correction in degrees (model forward vs. world forward). */
    avatarRotationOffset: number;
    /** Eye height — matches CharacterController.eyeHeight for feet placement. */
    eyeHeight: number;
    /** Extra visual downward offset matching CharacterController.hoverHeight. */
    groundOffset: number;
}

export const avatarConfig: AvatarConfig = {
    avatarUrl: '/assets/avatars/man/player.glb',
    idleAnim: 'Idle',
    walkAnim: 'Walking',
    runAnim: 'Run',
    jumpAnim: 'Jump',
    actionAnims: [
        { name: 'Goodbye', label: '再见' },
        { name: 'HandRaising', label: '举手' },
        { name: 'Waving', label: '挥手' },
        { name: 'Clap', label: '鼓掌' },
        { name: 'Cheering', label: '欢呼' },
        { name: 'Dancing', label: '跳舞' },
        { name: 'Kneel', label: '跪下' }
    ],
    avatarScale: 1,
    avatarRotationOffset: 0,
    eyeHeight: 1.3,
    groundOffset: 0.2
};

/**
 * 人物整体缩小系数（2026-10-14 需求：高度减约一个头——7.5 头身 ≈ ×0.87）。
 * 在模型挂载点统一相乘（AvatarController 本地+远端模型、WorldUI
 * 角色舞台预览），不改 config.avatarScale 的语义：后端 avatars.json / DB
 * config_json 原样下发（仍为 1），未来按形象微调 avatarScale 时以本系数为
 * 基准叠加。恢复原大小改回 1 即可。
 */
export const AVATAR_SIZE_SCALE = 0.87;
